<template>
    <div class="pill-wrapper" :class="{ active: selectedCount > 0 }">
  <span
    class="pill-button"
    @click="onClick"
  >
    <span class="label">{{ label }}</span>
    <span v-if="selectedCount" class="selected-count">{{ selectedCount }}</span>
    <span v-if="selectedCount" class="divider"></span>
    <span v-else class="arrow-icon"></span>
  </span>
    <button  v-if="selectedCount" 
      class="clear-button" 
      @click="resetSelections"
    >
      <span class="clear-icon"></span>
    </button>
    <Modal
      ref="modal"
      :is-open="isModalOpen"
      :title="label"
      :count="selectedCount"
      :labels="labels"
      @close="closeModal"
      @reset="resetSelections"
      @search="onSearch"
    >
      <FacetOption 
        v-for="opt in filteredOptions" 
        :key="opt.id"
        :label="opt.name" 
        :value="facetId + '=' + opt.id" 
        :count="opt.count"
        :checked="isSelected(opt.id)"
        @change="toggleOption(opt.id)"
      />
      <template #modal-footer>
        <button v-if="!showBackButton || selectedCount > 0"
          class="select-button" 
          :disabled="selectedCount === 0"
          @click="onApply"
        >
          <span>{{ buttonLabel }}</span>
        </button>
        <button v-else
          class="back-button" 
          @click="closeModal"
        >
          <span>{{ labels.back }}</span>
          </button>
      </template>
    </Modal>
</div>
</template>

<script>
import Modal from './Modal.vue';
import FacetOption from './FacetOption.vue';

export default {
    name: 'Pill',
    components: {
        Modal,
        FacetOption
    },
    props: {
        label: String,
        options: Object,
        isModalOpen: Boolean,
        facetId: String,
        showBackButton: Boolean,
        initiallySelectedOptions: {
            type: Array,
            default: () => []
        },
        labels: Object
    },
    data() {
        return {
            searchQuery: '',
            selectedOptions: this.initiallySelectedOptions
        }
    },
    computed: {
        selectedCount() {
            return this.selectedOptions.length;
        },
        filteredOptions() {
            if (!this.searchQuery) return this.options;
            
            const query = this.searchQuery.toLowerCase();
            return this.options.filter(opt => 
                opt.name.toLowerCase().includes(query)
            );
        },
        totalResults() {
            return this.selectedOptions.reduce((total, optionId) => {
                const id = optionId.split('=')[1];
                const option = this.options.find(opt => opt.id === id);
                return total + (option ? option.count : 0);
            }, 0);
        },
        buttonLabel() {
            if (this.selectedCount === 0) {
                return this.labels.selectFilter;
            }
            return this.labels.showResults.replace('{n}', this.totalResults);
        }
    },
    methods: {
        onClick() {
            this.$emit('toggle');
        },
        closeModal() {
            this.$emit('toggle');
            this.selectedOptions = this.initiallySelectedOptions;
        },
        onApply() {
            this.$emit('apply', this.selectedOptions);
            this.$emit('toggle');
        },
        resetSelections() {
            this.selectedOptions = [];
            this.searchQuery = '';
            this.$emit('reset');
        },
        toggleOption(optionId) {
            const fullId = `${this.facetId}=${optionId}`;
            if (this.selectedOptions.includes(fullId)) {
                this.selectedOptions = this.selectedOptions.filter(id => id !== fullId);
            } else {
                this.selectedOptions.push(fullId);
            }
        },
        isSelected(optionId) {
            return this.selectedOptions.includes(`${this.facetId}=${optionId}`);
        },
        onSearch(query) {
            this.searchQuery = query;
        }
    },
    emits: ['toggle', 'apply'],
    watch: {
        isModalOpen(newValue, oldValue) {
            if (oldValue === true && newValue === false) {
                this.selectedOptions = [...this.initiallySelectedOptions];
            }
        },
        initiallySelectedOptions(newValue) {
            this.selectedOptions = [...newValue];
        }
    }
}
</script>

<style scoped>
.pill-wrapper {
  display: inline-flex;
  width: fit-content;
  align-items: center;
  height: 27px;
  border-radius: 15px;
  background-color: white;
  color: #2655ff;
  border: 1px solid #2655ff;
  cursor: pointer;
  box-sizing: border-box;
  font-family: 'Gotham SSm A','Gotham SSm B',sans-serif;
  font-size: 14px;
  font-weight: 500;
  font-style: normal;
  margin-right: 8px;
  position: relative;
}

.pill-wrapper.active {
  background-color: #2655ff;
  color: white;
}

.pill-button {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
}

.label {
  padding: 8px 0px 8px 16px;
}

.selected-count {
  padding: 8px 16px 8px 8px;
}

.divider {
  width: 1px;
  height: 27px;
  background-color: white;
  display: inline-block;
}

.arrow-icon {
  width: 10px;
  height: 10px;
  display: inline-block;
  background-image: url("data:image/svg+xml,%3Csvg width='10' height='7' viewBox='0 0 10 7' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.00048 4.28132L8.30048 0.981323L9.24315 1.92399L5.00048 6.16666L0.757812 1.92399L1.70048 0.981323L5.00048 4.28132Z' fill='%232655FF'/%3E%3C/svg%3E ");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  margin: 0 8px;
}

.clear-button {
  border: none;
  background: none;
  padding: 0px 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.clear-icon {
  width: 10px;
  height: 10px;
  display: block;
  background-image: url("data:image/svg+xml,%3Csvg width='10' height='11' viewBox='0 0 10 11' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.65625 1.77494L8.72506 0.84375L5 4.56881L1.27494 0.84375L0.34375 1.77494L4.06881 5.5L0.34375 9.22506L1.27494 10.1562L5 6.43119L8.72506 10.1562L9.65625 9.22506L5.93119 5.5L9.65625 1.77494Z' fill='white'/%3E%3C/svg%3E ");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  cursor: pointer;
  
}

.pill-wrapper.active .arrow-icon {
  background-image: url("data:image/svg+xml,%3Csvg width='10' height='7' viewBox='0 0 10 7' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4.99999 2.71871L1.69999 6.01871L0.757324 5.07604L4.99999 0.833374L9.24266 5.07604L8.29999 6.01871L4.99999 2.71871Z' fill='white'/%3E%3C/svg%3E%0A");
}

.select-button {
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 4px;
  background-color: #2655ff;
  color: white;
  font-weight: 500;
  font-size: 16px;
  font-family: inherit;
  cursor: pointer;
}

.select-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.back-button {
  width: 100%;
  padding: 12px;
  border: solid 1px #2655ff;
  border-radius: 4px;
  background-color: white;
  color: #2655ff;
  font-weight: 500;
  font-size: 16px;
  font-family: inherit;
  cursor: pointer;
}
</style>
